import { cn } from "@simpro/ui";
import type { ReactNode } from "react";
import { useSalonClock } from "../hooks/useSalonClock";
import { salonNow, weekRows } from "../lib/openingHours";
import type { BranchHour, Holiday } from "../schemas/salon.schemas";

export interface OpeningHoursProps {
  hours: BranchHour[];
  holidays: Holiday[];
  /** Under the days: the address and directions, where the card stands beside the menu (round 2 C18). */
  footer?: ReactNode;
}

/**
 * The opening week (design note salon-booking-design-2.md H1): Monday first, equal days folded
 * together, today picked out, and the next days it is shut underneath.
 *
 * It sits in the Visit section as the Organic Wellness board's sand card (design note
 * salon-booking-organic-wellness.md section 6 item 10): the days on soft rows, today a pale sage pill.
 *
 * The times are wall-clock times in the salon's own zone, so converting them to the reader's device
 * would be wrong for anyone travelling. They are written on the salon's chosen clock (FR-SA-140).
 */
export function OpeningHours({ hours, holidays, footer }: OpeningHoursProps) {
  const { clock, timeZone } = useSalonClock();
  const rows = weekRows(hours, salonNow(timeZone).day, clock);

  return (
    <section aria-label="Opening hours" className="flex flex-col gap-4 rounded-[1.75rem] bg-surface p-6 text-[15px]">
      <h3 className="px-3 text-sm font-semibold text-subtle">Opening hours</h3>
      <dl className="flex flex-col gap-2">
        {rows.map((row) => (
          <div
            key={row.label}
            className={cn(
              "grid grid-cols-[1fr_auto] gap-3 rounded-full px-3 py-1",
              row.today ? "bg-sage-tint font-semibold text-heading" : "text-content",
            )}
          >
            <dt>{row.label}</dt>
            <dd className="tabular-nums">{row.value}</dd>
          </div>
        ))}
      </dl>
      {holidays.length > 0 ? (
        <ul className="flex flex-col gap-2">
          {holidays.map((holiday) => (
            <li
              key={holiday.date}
              className="rounded-2xl bg-base px-4 py-3 text-[13px] text-heading lg:text-sm"
            >
              <b className="font-semibold">Closed {formatDate(holiday.date)}</b> for {holiday.name}.
            </li>
          ))}
        </ul>
      ) : null}
      {footer ? <div className="px-3 pt-1 text-muted text-[13px] lg:text-sm">{footer}</div> : null}
    </section>
  );
}

/** "Sun 25 Oct". */
function formatDate(value: string): string {
  return new Date(`${value}T00:00:00`)
    .toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" })
    .replace(",", "");
}
